import React, { useState, useEffect } from 'react';
import { FaUserMd, FaLeaf, FaDumbbell } from "react-icons/fa";
import { ArrowRight } from "lucide-react";

// Import your local images from the img folder
import service1 from '../img/service11.png'; // For "How can I help you" section
import service2 from '../img/service12.png'; // For "What we offer" section
import service3 from '../img/service13.png'; // For "What to expect" section

const ServicesPage = ({ onOpenEnquiryForm }) => {
  const [selectedService, setSelectedService] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const services = [
    { id: 1, title: 'General Wellness' },
    { id: 2, title: 'Diet For Kids' },
    { id: 3, title: 'Weight Management' },
    { id: 4, title: 'Pregnancy Nutritional Care' },
    { id: 5, title: 'Diabetic Diet' },
    { id: 6, title: 'PCOD / PCOS Diet' },
    { id: 7, title: 'Hypertension/Cardiac Care' },
    { id: 8, title: 'Diets For All Medical Conditions' }
  ];

  const expectations = [
    { id: 1, title: 'Personalised, science-backed nutrition plan - No FAD diets' },
    { id: 2, title: 'Easy-to-follow meal schedule that fits your routine' },
    { id: 3, title: 'Flexible guidance for travel & busy days' },
    { id: 4, title: 'Continuous follow-ups & personalised support throughout your wellness journey' },
    { id: 5, title: 'Clear instructions to reduce anxiety on food planning' },
    { id: 6, title: 'Long-term habit-building for sustainable results' }
  ];

  const handleBookConsultation = () => {
    if (onOpenEnquiryForm) {
      onOpenEnquiryForm();
    }
  };

  return (
    <div style={{ overflowX: 'hidden' }}>
      {selectedService ? (
        <ServiceDetailPage 
          selectedService={selectedService} 
          setSelectedService={setSelectedService}
          onOpenEnquiryForm={onOpenEnquiryForm}
        />
      ) : (
        <MainServicesPage 
          isMobile={isMobile}
          services={services}
          expectations={expectations}
          handleBookConsultation={handleBookConsultation}
        />
      )}
    </div>
  );
};

const ServiceDetailPage = ({ selectedService, setSelectedService, onOpenEnquiryForm }) => (
  <section style={{ padding: '40px 20px', background: '#fff' }}>
    <button 
      style={{
        background: 'none',
        border: '2px solid #4a7c59',
        padding: '12px 24px',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#4a7c59',
        cursor: 'pointer',
        marginBottom: '40px'
      }}
      onClick={() => setSelectedService(null)}
    >
      ← Back to Services
    </button>
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '60px' }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: selectedService.bgColor,
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2.5rem',
          margin: '0 auto 20px'
        }}>
          <span>{selectedService.icon}</span>
        </div>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '800', color: '#1f2937', marginBottom: '10px' }}>{selectedService.title}</h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280' }}>{selectedService.description}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '15px' }}>Overview</h2>
          <p style={{ fontSize: '1.05rem', color: '#4b5563', lineHeight: '1.7' }}>{selectedService.detailedContent.overview}</p>
        </div>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '15px' }}>Key Benefits</h2>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedService.detailedContent.benefits.map((benefit, index) => (
              <li key={index} style={{ fontSize: '1.05rem', color: '#4b5563', paddingLeft: '30px', position: 'relative', lineHeight: '1.6' }}>
                <span style={{ position: 'absolute', left: 0, color: '#4a7c59', fontWeight: 'bold' }}>✓</span>
                {benefit}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '15px' }}>Our Approach</h2>
          <p style={{ fontSize: '1.05rem', color: '#4b5563', lineHeight: '1.7' }}>{selectedService.detailedContent.approach}</p>
        </div>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '15px' }}>Who Is This For?</h2>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedService.detailedContent.whoItsFor.map((person, index) => (
              <li key={index} style={{ fontSize: '1.05rem', color: '#4b5563', paddingLeft: '30px', position: 'relative', lineHeight: '1.6' }}>
                <span style={{ position: 'absolute', left: 0, color: '#4a7c59', fontWeight: 'bold' }}>•</span>
                {person}
              </li>
            ))}
          </ul>
        </div>
        <div style={{ 
          background: 'linear-gradient(135deg, #4a7c59 0%, #5a9068 100%)',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          marginTop: '20px'
        }}>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#fff', marginBottom: '10px' }}>Ready to Get Started?</h2>
          <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.9)', marginBottom: '25px' }}>Book a consultation today and take the first step towards your health goals.</p>
          <button 
            style={{
              background: '#fff',
              border: 'none',
              padding: '16px 40px',
              borderRadius: '50px',
              fontSize: '1.05rem',
              fontWeight: '600',
              color: '#4a7c59',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
            onClick={() => onOpenEnquiryForm && onOpenEnquiryForm()}
          >
            Book Consultation
          </button>
        </div>
      </div>
    </div>
  </section>
);

const MainServicesPage = ({ isMobile, services, expectations, handleBookConsultation }) => (
  <>
    {/* How can I help you section - using service1 */}
    <section style={{
      backgroundImage: `url(${service1})`,
      backgroundAttachment: isMobile ? 'scroll' : 'fixed',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      position: 'relative',
      overflowX: 'hidden',
      padding: isMobile ? '60px 20px' : '100px 20px',
      minHeight: '600px',
      filter: 'contrast(1.1) brightness(1.05)',
      WebkitFilter: 'contrast(1.1) brightness(1.05)'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1
      }}></div>
      <div style={{ position: 'relative', zIndex: 2 }}>
        <h1 style={{ 
          fontSize: isMobile ? '2rem' : '3rem', 
          fontWeight: '800', 
          color: '#fff', 
          textAlign: 'center', 
          marginBottom: '15px',
          textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
        }}>
          How can I help you?
        </h1>
        <p style={{ 
          fontSize: isMobile ? '1rem' : '1.2rem', 
          color: 'rgba(255,255,255,0.95)', 
          textAlign: 'center', 
          marginBottom: '60px',
          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
        }}>
          Choose the perfect plan that fits your wellness journey and goals.
        </p>
        <Pricing onOpenEnquiryForm={handleBookConsultation} />
      </div>
    </section>

    {/* What we offer section - using service2 */}
    <section style={{ overflowX: 'hidden', padding: isMobile ? '40px 20px' : '80px 20px', background: '#fff' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {isMobile ? (
          <>
            <div style={{ borderLeft: '4px solid #4a7c59', paddingLeft: '20px', marginBottom: '40px' }}>
              <h1 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '10px' }}>What we offer</h1>
            </div>
            <div style={{ position: 'relative', width: '100%', marginBottom: '40px' }}>
              <img 
                src={service2}
                alt="Nutritionist preparing healthy food" 
                loading="eager"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  minHeight: '400px',
                  borderRadius: '20px', 
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  filter: 'contrast(1.05) brightness(1.02) saturate(1.1)',
                  WebkitFilter: 'contrast(1.05) brightness(1.02) saturate(1.1)',
                  imageRendering: 'crisp-edges',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden'
                }} 
              />
              <div style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{ fontSize: '1.3rem' }}>🚀</div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#4a7c59', margin: 0 }}>100+</h4>
                  <p style={{ fontSize: '0.7rem', color: '#6b7280', margin: 0 }}>Achieving Wellness</p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {services.map(service => (
                <div key={service.id} style={{ 
                  padding: '20px 24px', 
                  background: '#f9fafb',
                  borderRadius: '12px',
                  borderLeft: '4px solid #4a7c59',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>{service.title}</h3>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: '60px', 
            alignItems: 'center' 
          }}>
            <div style={{ flex: 1, width: '100%' }}>
              <div style={{ borderLeft: '4px solid #4a7c59', paddingLeft: '20px', marginBottom: '40px' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '10px' }}>What we offer</h1>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {services.map(service => (
                  <div key={service.id} style={{ 
                    padding: '20px 24px', 
                    background: '#f9fafb',
                    borderRadius: '12px',
                    borderLeft: '4px solid #4a7c59',
                    transition: 'all 0.3s ease',
                    cursor: 'default'
                  }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>{service.title}</h3>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ position: 'relative', flex: 1, width: '100%' }}>
              <img 
                src={service2}
                alt="Nutritionist preparing healthy food" 
                loading="eager"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '20px', 
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  imageRendering: 'high-quality',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }} 
              />
              <div style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '20px 30px',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <div style={{ fontSize: '2rem' }}>🚀</div>
                <div>
                  <h4 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#4a7c59', margin: 0 }}>100+</h4>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>Achieving Wellness</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>

    {/* What to expect section - using service3 */}
    <section style={{ overflowX: 'hidden', padding: isMobile ? '40px 20px' : '80px 20px', background: '#f9fafb' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {isMobile ? (
          <>
            <div style={{ borderLeft: '4px solid #4a7c59', paddingLeft: '20px', marginBottom: '20px' }}>
              <h1 style={{ fontSize: '1.8rem', fontWeight: '700', color: '#1f2937', marginBottom: '10px' }}>What to expect from your consultation</h1>
              <p style={{ fontSize: '0.95rem', color: '#6b7280', lineHeight: '1.6' }}>Your detailed and exact-timed diet will significantly improve your overall health and well-being.</p>
            </div>
            <div style={{ position: 'relative', width: '100%', marginBottom: '40px', marginTop: '40px' }}>
              <img 
                src={service3}
                alt="Nutritionist consultation" 
                loading="eager"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '20px', 
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  imageRendering: 'high-quality',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }} 
              />
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '12px 20px',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <div style={{ fontSize: '1.3rem' }}>☁️</div>
                <div>
                  <h4 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#4a7c59', margin: 0 }}>100+</h4>
                  <p style={{ fontSize: '0.7rem', color: '#6b7280', margin: 0 }}>Healthy Recipes</p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {expectations.map((expectation) => (
                <div key={expectation.id} style={{ 
                  padding: '20px 24px', 
                  background: '#fff',
                  borderRadius: '12px',
                  borderLeft: '4px solid #4a7c59',
                  transition: 'all 0.3s ease',
                  cursor: 'default'
                }}>
                  <p style={{ fontSize: '0.95rem', fontWeight: '500', color: '#1f2937', margin: 0, lineHeight: '1.5' }}>{expectation.title}</p>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: '60px', 
            alignItems: 'center' 
          }}>
            <div style={{ position: 'relative', flex: 1, width: '100%' }}>
              <img 
                src={service3}
                alt="Nutritionist consultation" 
                loading="eager"
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: '20px', 
                  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  imageRendering: 'high-quality',
                  WebkitBackfaceVisibility: 'hidden',
                  backfaceVisibility: 'hidden',
                  transform: 'translateZ(0)',
                  willChange: 'transform'
                }} 
              />
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '20px 30px',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <div style={{ fontSize: '2rem' }}>☁️</div>
                <div>
                  <h4 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#4a7c59', margin: 0 }}>100+</h4>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0 }}>Healthy Recipes</p>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, width: '100%' }}>
              <div style={{ borderLeft: '4px solid #4a7c59', paddingLeft: '20px', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '10px' }}>What to expect from your consultation</h1>
                <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: '1.6' }}>Your detailed and exact-timed diet will significantly improve your overall health and well-being.</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '40px' }}>
                {expectations.map((expectation) => (
                  <div key={expectation.id} style={{ 
                    padding: '20px 24px', 
                    background: '#fff',
                    borderRadius: '12px',
                    borderLeft: '4px solid #4a7c59',
                    transition: 'all 0.3s ease',
                    cursor: 'default'
                  }}>
                    <p style={{ fontSize: '1rem', fontWeight: '500', color: '#1f2937', margin: 0, lineHeight: '1.5' }}>{expectation.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  </>
);

// Replace the Pricing component with this one (from your second Pricing.jsx)
const Pricing = ({ onOpenEnquiryForm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const pricingPlans = [
    {
      icon: <FaLeaf size={26} />,
      title: "1-MONTH HEALTHY EATING PROGRAM",
      subtitle: "Build healthy & sustainable eating habits.",
      price: "3999",
      priceDetails: "/30 days",
      features: [
        "Full assessment",
        "Personalised diet chart",
        "Diet explanation call",
        "Weekly check-ins",
        "Recipes & meal swaps",
        "Plan modifications",
      ],
      idealFor: "Anyone who wants consistency, discipline & better habits.",
      buttonText: "JOIN 1-MONTH PLAN",
      isFeatured: false,
    },
    {
      icon: <FaUserMd size={26} />,
      title: "ONE-TIME NUTRITION CONSULTATION",
      subtitle: "Quick expert guidance tailored for you.",
      price: "699",
      priceDetails: "/session",
      features: [
        "30–40 min consultation",
        "Nutritional assessment",
        "Goal-setting + diet advice",
        "1 follow-up message (within 7 days)",
        "Next-step guidance",
      ],
      idealFor: "First-time clients seeking clarity before starting a long plan.",
      buttonText: "BOOK CONSULTATION",
      isFeatured: true,
    },
    {
      icon: <FaDumbbell size={26} />,
      title: "100 DAYS DIET PROGRAM",
      subtitle: "Transform your metabolism sustainably.",
      price: "10999",
      priceDetails: "/100 days",
      features: [
        "Detailed nutritional assessment",
        "Custom 100-day diet plan",
        "Explanation call with family",
        "Daily accountability",
        "Fortnightly reviews",
        "Lifestyle-based plan changes",
      ],
      idealFor: "Clients seeking long-term transformation & balance.",
      buttonText: "JOIN 100 DAYS PROGRAM",
      isFeatured: false,
    },
  ];

  const handleButtonClick = () => {
    if (onOpenEnquiryForm) {
      onOpenEnquiryForm();
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section style={styles.section}>
      <div style={styles.container}>
        {isMobile ? (
          <div style={styles.carouselContainer}>
            <div style={styles.carouselWrapper}>
              <div 
                style={{
                  ...styles.carouselTrack,
                  transform: `translateX(-${currentSlide * 100}%)`,
                }}
              >
                {pricingPlans.map((plan, i) => (
                  <div key={i} style={styles.carouselSlide}>
                    <PricingCard 
                      plan={plan} 
                      handleButtonClick={handleButtonClick}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.dotsContainer}>
              {pricingPlans.map((_, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.dot,
                    ...(currentSlide === index ? styles.activeDot : {}),
                  }}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.wrapper}>
            {pricingPlans.map((plan, i) => (
              <PricingCard 
                key={i}
                plan={plan} 
                handleButtonClick={handleButtonClick}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const PricingCard = ({ plan, handleButtonClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        ...styles.card,
        ...(plan.isFeatured ? styles.featuredCard : styles.regularCard),
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        ...styles.iconBox,
        ...(plan.isFeatured ? styles.featuredIconBox : styles.regularIconBox)
      }}>
        {plan.icon}
      </div>

      <h2 style={{
        ...styles.title,
        ...(plan.isFeatured ? styles.featuredTitle : styles.regularTitle)
      }}>
        {plan.title}
      </h2>
      <p style={{
        ...styles.subtitle,
        ...(plan.isFeatured ? styles.featuredSubtitle : styles.regularSubtitle)
      }}>
        {plan.subtitle}
      </p>

      <div style={styles.priceBox}>
        <span style={{
          ...styles.currency,
          ...(plan.isFeatured ? styles.featuredCurrency : styles.regularCurrency)
        }}>
          ₹
        </span>
        <span style={{
          ...styles.price,
          ...(plan.isFeatured ? styles.featuredPrice : styles.regularPrice)
        }}>
          {plan.price}
        </span>
        <span style={{
          ...styles.duration,
          ...(plan.isFeatured ? styles.featuredDuration : styles.regularDuration)
        }}>
          {plan.priceDetails}
        </span>
      </div>

      <ul style={styles.list}>
        {plan.features.map((f, index) => (
          <li key={index} style={{
            ...styles.listItem,
            ...(plan.isFeatured ? styles.featuredListItem : styles.regularListItem)
          }}>
            ✓ {f}
          </li>
        ))}
      </ul>

      <div style={{
        ...styles.idealBlock,
        ...(plan.isFeatured ? styles.featuredIdealBlock : styles.regularIdealBlock)
      }}>
        <p style={{
          ...styles.idealTitle,
          ...(plan.isFeatured ? styles.featuredIdealTitle : styles.regularIdealTitle)
        }}>
          Ideal for
        </p>
        <p style={{
          ...styles.idealText,
          ...(plan.isFeatured ? styles.featuredIdealText : styles.regularIdealText)
        }}>
          {plan.idealFor}
        </p>
      </div>

      <InteractiveHoverButton 
        onClick={handleButtonClick}
        isFeatured={plan.isFeatured}
      >
        {plan.buttonText}
      </InteractiveHoverButton>
    </div>
  );
};

const InteractiveHoverButton = ({ children, onClick, isFeatured }) => {
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  return (
    <button
      style={{
        ...styles.button,
        ...(isFeatured ? styles.featuredButton : styles.regularButton),
        position: "relative",
        overflow: "hidden",
      }}
      onClick={onClick}
      onMouseEnter={() => setIsButtonHovered(true)}
      onMouseLeave={() => setIsButtonHovered(false)}
    >
      <div style={styles.buttonContent}>
        <div style={{
          ...styles.buttonDot,
          ...(isFeatured ? styles.featuredButtonDot : styles.regularButtonDot),
          transform: isButtonHovered ? "scale(80)" : "scale(1)",
        }}></div>
        <span style={{
          ...styles.buttonText,
          transform: isButtonHovered ? "translateX(50px)" : "translateX(0)",
          opacity: isButtonHovered ? 0 : 1,
        }}>
          {children}
        </span>
      </div>
      <div style={{
        ...styles.buttonHoverContent,
        ...(isFeatured ? styles.featuredButtonHoverText : styles.regularButtonHoverText),
        transform: isButtonHovered ? "translateX(-20px)" : "translateX(50px)",
        opacity: isButtonHovered ? 1 : 0,
      }}>
        <span>{children}</span>
        <ArrowRight size={18} />
      </div>
    </button>
  );
};

const styles = {
  section: {
    background: "transparent",
    padding: "0 20px",
    display: "flex",
    justifyContent: "center",
    marginTop: "60px",
  },
  container: {
    width: "100%",
    maxWidth: "1200px",
  },
  wrapper: {
    display: "flex",
    gap: "20px",
    width: "100%",
    justifyContent: "center",
    flexWrap: "nowrap",
    alignItems: "stretch",
  },
  carouselContainer: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  carouselWrapper: {
    width: "100%",
    maxWidth: "500px",
    overflow: "hidden",
    position: "relative",
  },
  carouselTrack: {
    display: "flex",
    transition: "transform 0.4s ease-in-out",
    width: "100%",
  },
  carouselSlide: {
    minWidth: "100%",
    display: "flex",
    justifyContent: "center",
    padding: "0 10px",
    boxSizing: "border-box",
  },
  dotsContainer: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    marginTop: "20px",
  },
  dot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    background: "rgba(255, 255, 255, 0.5)",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    padding: 0,
  },
  activeDot: {
    background: "rgba(255, 255, 255, 0.95)",
    width: "32px",
    borderRadius: "6px",
  },
  card: {
    borderRadius: "14px",
    padding: "25px 20px",
    width: "100%",
    maxWidth: "380px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
    transition: "0.3s ease",
    display: "flex",
    flexDirection: "column",
    minHeight: "520px",
    flex: "1",
  },
  regularCard: {
    background: "#ffffff",
  },
  featuredCard: {
    background: "#009e69",
    color: "#ffffff",
  },
  iconBox: {
    width: "52px",
    height: "52px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "16px",
  },
  regularIconBox: {
    background: "#e8f5ea",
    color: "#009e69",
  },
  featuredIconBox: {
    background: "rgba(255, 255, 255, 0.2)",
    color: "#ffffff",
  },
  title: {
    fontSize: "1.1rem",
    fontWeight: "700",
    marginBottom: "10px",
    lineHeight: "1.3",
  },
  regularTitle: {
    color: "#1f2937",
  },
  featuredTitle: {
    color: "#ffffff",
  },
  subtitle: {
    fontSize: "0.9rem",
    marginBottom: "20px",
    lineHeight: "1.4",
  },
  regularSubtitle: {
    color: "#6b7280",
  },
  featuredSubtitle: {
    color: "rgba(255, 255, 255, 0.9)",
  },
  priceBox: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    marginBottom: "20px",
  },
  currency: {
    fontSize: "1.1rem",
    fontWeight: "600",
  },
  regularCurrency: {
    color: "#009e69",
  },
  featuredCurrency: {
    color: "#ffffff",
  },
  price: {
    fontSize: "2.1rem",
    fontWeight: "800",
  },
  regularPrice: {
    color: "#111827",
  },
  featuredPrice: {
    color: "#ffffff",
  },
  duration: {
    fontSize: "0.9rem",
  },
  regularDuration: {
    color: "#6b7280",
  },
  featuredDuration: {
    color: "rgba(255, 255, 255, 0.8)",
  },
  list: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 20px",
    flex: "1",
  },
  listItem: {
    fontSize: "0.9rem",
    marginBottom: "8px",
    lineHeight: "1.4",
  },
  regularListItem: {
    color: "#374151",
  },
  featuredListItem: {
    color: "rgba(255, 255, 255, 0.95)",
  },
  idealBlock: {
    padding: "14px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  regularIdealBlock: {
    background: "#f3f4f6",
  },
  featuredIdealBlock: {
    background: "rgba(255, 255, 255, 0.15)",
  },
  idealTitle: {
    fontWeight: "600",
    fontSize: "0.9rem",
    marginBottom: "6px",
  },
  regularIdealTitle: {
    color: "#111827",
  },
  featuredIdealTitle: {
    color: "#ffffff",
  },
  idealText: {
    fontSize: "0.82rem",
    lineHeight: "1.4",
  },
  regularIdealText: {
    color: "#4b5563",
  },
  featuredIdealText: {
    color: "rgba(255, 255, 255, 0.9)",
  },
  button: {
    border: "none",
    padding: "14px",
    borderRadius: "9999px",
    fontSize: "0.95rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    width: "100%",
    position: "relative",
    overflow: "hidden",
  },
  regularButton: {
    background: "#ffffff",
    color: "#009e69",
    border: "2px solid #009e69",
  },
  featuredButton: {
    background: "#ffffff",
    color: "#009e69",
    border: "2px solid #ffffff",
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  buttonDot: {
    height: "8px",
    width: "8px",
    borderRadius: "50%",
    transition: "all 0.3s ease",
  },
  regularButtonDot: {
    background: "#009e69",
  },
  featuredButtonDot: {
    background: "#009e69",
  },
  buttonText: {
    display: "inline-block",
    transition: "all 0.3s ease",
  },
  buttonHoverContent: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    display: "flex",
    height: "100%",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },
  regularButtonHoverText: {
    color: "#ffffff",
  },
  featuredButtonHoverText: {
    color: "#ffffff",
  },
};

export default ServicesPage;
